// ==========================================================================
// Shaka Player Plugin (DASH + DRM: ClearKey / Widevine / FairPlay)
// - DRM + Safari: 네이티브 HLS + EME FairPlay (Shaka 미사용)
// - DRM + Chrome/Firefox (drmType='widevine'): Shaka Player + Widevine
// - DRM + Chrome/Firefox (drmType='clearkey'): Shaka Player + ClearKey
// - DRM 없음 + .mpd: 일반 DASH 재생
// - hls.js는 기존 그대로 유지
// ==========================================================================

import shaka from 'shaka-player/dist/shaka-player.compiled';
import is from '../utils/is';

// ---------------------------------------------------------------------------
// Logging helper
// ---------------------------------------------------------------------------

function _log(drm, level, msg) {
  console.log(`[ShakaPlugin] ${msg}`);
  if (drm && typeof drm.onLog === 'function') drm.onLog(level, msg);
}

// ---------------------------------------------------------------------------
// FairPlay 감지
// ---------------------------------------------------------------------------

function _detectFairPlay() {
  return typeof window !== 'undefined' && !!window.WebKitMediaKeys;
}

// ---------------------------------------------------------------------------
// ClearKey (Chrome/Firefox — Shaka Player)
// ---------------------------------------------------------------------------

function _configureClearKey(sp, drm, baseUrl) {
  sp.configure({
    drm: {
      servers: {
        'org.w3.clearkey': baseUrl + '/api/drm/clearkey',
      },
    },
    abr: { enabled: true },
  });
}

function _registerNetworkFilters(sp, drm) {
  const net = sp.getNetworkingEngine();
  if (!net) return;

  // ECDH P-256 키쌍: 첫 라이선스 요청 시 lazy 생성 (재생 세션당 1회)
  let ecdhKeysPromise = null;
  function _ensureECDHKeys() {
    if (!ecdhKeysPromise) ecdhKeysPromise = _generateECDHKeyPair();
    return ecdhKeysPromise;
  }

  net.registerRequestFilter(async (type, request) => {
    try {
      if (type !== shaka.net.NetworkingEngine.RequestType.LICENSE) return;

      const ecdhKeys = await _ensureECDHKeys();

      let token = null;
      try {
        token = typeof drm.getToken === 'function' ? await drm.getToken() : drm.getToken;
      } catch (tokenErr) {
        console.warn('[ShakaPlugin] getToken failed:', tokenErr);
      }

      if (token) request.headers['Authorization'] = `Bearer ${token}`;
      if (drm.sessionToken) request.headers['X-Session-Token'] = drm.sessionToken;
      if (drm.deviceId) request.headers['X-Device-ID'] = drm.deviceId;
      if (drm.userId) request.headers['X-User-ID'] = drm.userId;
      request.headers['X-Drm-Public-Key'] = ecdhKeys.publicKeyBase64;

      // ClearKey: JSON body에 content_id 주입
      try {
        const body = JSON.parse(new TextDecoder().decode(request.body));
        body.content_id = drm.contentId;
        body.user_id = drm.userId || '';
        request.body = new TextEncoder().encode(JSON.stringify(body));
        request.headers['Content-Type'] = 'application/json';
      } catch (_) {
        // body가 JSON이 아닌 경우 무시
      }
    } catch (filterErr) {
      console.error('[ShakaPlugin] Request filter error:', filterErr);
    }
  });

  // 응답 필터: ECDH 암호화된 ClearKey 응답 복호화
  net.registerResponseFilter(async (type, response) => {
    if (type !== shaka.net.NetworkingEngine.RequestType.LICENSE) return;
    try {
      const text = new TextDecoder().decode(response.data);
      const json = JSON.parse(text);

      if (json.algorithm === 'ecdh-aes-256-gcm' && json.encrypted && json.server_public_key) {
        const ecdhKeys = await _ensureECDHKeys();
        const decrypted = await _ecdhDecrypt(ecdhKeys.privateKey, json.server_public_key, json.encrypted);
        response.data = new TextEncoder().encode(decrypted);
      }
    } catch (_) {
      // 평문 응답이거나 JSON이 아닌 경우 무시
    }
  });
}

// ---------------------------------------------------------------------------
// Widevine (Chrome/Firefox — Shaka Player)
// ---------------------------------------------------------------------------

function _configureWidevine(sp, drm, baseUrl) {
  sp.configure({
    drm: {
      servers: {
        'com.widevine.alpha': baseUrl + '/api/drm/widevine',
      },
    },
    abr: { enabled: true },
  });
}

function _registerWidevineNetworkFilters(sp, drm) {
  const net = sp.getNetworkingEngine();
  if (!net) return;

  net.registerRequestFilter(async (type, request) => {
    try {
      if (type !== shaka.net.NetworkingEngine.RequestType.LICENSE) return;

      let token = null;
      try {
        token = typeof drm.getToken === 'function' ? await drm.getToken() : drm.getToken;
      } catch (tokenErr) {
        console.warn('[ShakaPlugin] getToken failed:', tokenErr);
      }

      if (token) request.headers['Authorization'] = `Bearer ${token}`;
      if (drm.sessionToken) request.headers['X-Session-Token'] = drm.sessionToken;
      if (drm.deviceId) request.headers['X-Device-ID'] = drm.deviceId;
      if (drm.userId) request.headers['X-User-ID'] = drm.userId;
      if (drm.contentId) request.headers['X-Content-ID'] = drm.contentId;

      // Widevine: body는 바이너리 protobuf (CDM이 생성) — 변환 없이 그대로 전송
    } catch (filterErr) {
      console.error('[ShakaPlugin] Widevine request filter error:', filterErr);
    }
  });

  // Widevine 응답은 바이너리 protobuf — 변환 불필요 (CDM이 직접 처리)
}

// ---------------------------------------------------------------------------
// ECDH P-256 + AES-256-GCM 키 교환 (Web Crypto API)
// ---------------------------------------------------------------------------

async function _generateECDHKeyPair() {
  const keyPair = await crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    false, // private key는 추출 불가
    ['deriveBits']
  );
  // 공개키를 raw 형식(65바이트 uncompressed P-256)으로 export → base64
  const pubRaw = await crypto.subtle.exportKey('raw', keyPair.publicKey);
  const publicKeyBase64 = btoa(String.fromCharCode(...new Uint8Array(pubRaw)));
  return { privateKey: keyPair.privateKey, publicKeyBase64 };
}

async function _ecdhDecrypt(clientPrivateKey, serverPubKeyBase64, encryptedBase64) {
  // 1. 서버 공개키 import
  const serverPubRaw = Uint8Array.from(atob(serverPubKeyBase64), c => c.charCodeAt(0));
  const serverPubKey = await crypto.subtle.importKey(
    'raw', serverPubRaw, { name: 'ECDH', namedCurve: 'P-256' }, false, []
  );

  // 2. ECDH → shared secret (32바이트)
  const sharedBits = await crypto.subtle.deriveBits(
    { name: 'ECDH', public: serverPubKey }, clientPrivateKey, 256
  );

  // 3. AES key = SHA-256(shared_secret)
  const hashBuffer = await crypto.subtle.digest('SHA-256', sharedBits);
  const aesKey = await crypto.subtle.importKey(
    'raw', hashBuffer, { name: 'AES-GCM' }, false, ['decrypt']
  );

  // 4. AES-256-GCM 복호화 (nonce 12 + ciphertext + tag 16)
  const raw = Uint8Array.from(atob(encryptedBase64), c => c.charCodeAt(0));
  const nonce = raw.slice(0, 12);
  const ciphertext = raw.slice(12);
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: nonce }, aesKey, ciphertext
  );
  return new TextDecoder().decode(decrypted);
}

// ---------------------------------------------------------------------------
// HLS 마스터 플레이리스트 파싱 (Safari 화질 목록 추출)
// ---------------------------------------------------------------------------

async function _fetchMasterPlaylist(hlsUrl, authHeaders) {
  try {
    const res = await fetch(hlsUrl, { headers: authHeaders });
    if (!res.ok) return { qualities: [], masterText: '', baseUrl: '' };
    const text = await res.text();
    const lines = text.split('\n');
    const qualities = [];

    // 마스터 playlist의 base URL (상대 경로 해석용)
    const baseUrl = hlsUrl.replace(/[?#].*$/, '').replace(/\/[^/]*$/, '/');

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line.startsWith('#EXT-X-STREAM-INF:')) continue;

      const resMatch = line.match(/RESOLUTION=(\d+)x(\d+)/);
      const bwMatch = line.match(/BANDWIDTH=(\d+)/);
      const nextLine = (lines[i + 1] || '').trim();
      if (!resMatch || !nextLine || nextLine.startsWith('#')) continue;

      // variant URL → 절대경로 변환
      let url;
      try {
        url = nextLine.startsWith('http') ? nextLine : new URL(nextLine, hlsUrl).href;
      } catch (_) {
        url = nextLine;
      }

      qualities.push({
        height: parseInt(resMatch[2], 10),
        width: parseInt(resMatch[1], 10),
        bandwidth: bwMatch ? parseInt(bwMatch[1], 10) : 0,
        url,
        streamInfLine: line,   // 원본 #EXT-X-STREAM-INF 라인
        variantLine: nextLine, // 원본 variant URL 라인
      });
    }

    return { qualities, masterText: text, baseUrl };
  } catch (_) {
    return { qualities: [], masterText: '', baseUrl: '' };
  }
}

// ---------------------------------------------------------------------------
// FairPlay (Safari — 네이티브 HLS + EME, Shaka 미사용)
// ---------------------------------------------------------------------------

async function _initFairPlay(player, hlsUrl) {
  const drm = player.config.drm;
  const baseUrl = drm.baseUrl || '';
  const videoEl = player.media;

  // 1. 인증서 fetch
  _log(drm, 'info', 'FairPlay: fetching certificate...');
  let cert = null;
  const token = typeof drm.getToken === 'function' ? await drm.getToken() : drm.getToken;
  try {
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (drm.deviceId) headers['X-Device-ID'] = drm.deviceId;
    const res = await fetch(baseUrl + '/api/drm/fairplay/cert', { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    cert = new Uint8Array(await res.arrayBuffer());
    _log(drm, 'success', `FairPlay cert fetched (${cert.byteLength} bytes)`);
  } catch (err) {
    _log(drm, 'error', `FairPlay cert fetch failed: ${err.message}`);
    throw err;
  }

  // 2. encrypted 이벤트 핸들러 (EME)
  let mediaKeys = null;
  const processedInitData = new Set();

  const onEncrypted = async (ev) => {
    const initDataHex = Array.from(new Uint8Array(ev.initData))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');

    if (processedInitData.has(initDataHex)) return;
    processedInitData.add(initDataHex);

    _log(drm, 'info', `FairPlay encrypted event: ${ev.initDataType} (${ev.initData.byteLength}B)`);

    try {
      // MediaKeys 생성 (최초 1회)
      if (!mediaKeys) {
        const config = [
          {
            initDataTypes: ['sinf', 'skd'],
            videoCapabilities: [{ contentType: 'video/mp4' }],
            audioCapabilities: [{ contentType: 'audio/mp4' }],
          },
        ];
        const access = await navigator.requestMediaKeySystemAccess('com.apple.fps', config);
        mediaKeys = await access.createMediaKeys();
        await mediaKeys.setServerCertificate(cert);
        await videoEl.setMediaKeys(mediaKeys);
        _log(drm, 'info', 'FairPlay MediaKeys created with server certificate');
      }

      const keySession = mediaKeys.createSession();

      keySession.addEventListener('message', async (msgEv) => {
        try {
          _log(drm, 'info', 'FairPlay SPC received, requesting CKC...');
          const freshToken = typeof drm.getToken === 'function' ? await drm.getToken() : drm.getToken;
          const res = await fetch(baseUrl + '/api/drm/fairplay/ksm', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/octet-stream',
              Authorization: `Bearer ${freshToken}`,
              'X-Session-Token': drm.sessionToken || '',
              'X-Device-ID': drm.deviceId || '',
              'X-Content-ID': drm.contentId || '',
              'X-User-ID': drm.userId || '',
            },
            body: msgEv.message,
          });
          if (!res.ok) throw new Error(`KSM HTTP ${res.status}`);
          const ckc = await res.arrayBuffer();
          await msgEv.target.update(ckc);
          _log(drm, 'success', `FairPlay CKC applied (${ckc.byteLength} bytes)`);
        } catch (err) {
          _log(drm, 'error', `FairPlay KSM error: ${err.message}`);
        }
      });

      keySession.addEventListener('keystatuseschange', (ev) => {
        const map = ev.target.keyStatuses;
        for (const [keyId, status] of map.entries()) {
          const hex = Array.from(new Uint8Array(keyId))
            .map((b) => b.toString(16).padStart(2, '0'))
            .join('');
          _log(drm, status === 'usable' ? 'success' : 'info', `Key ${hex}: ${status}`);
        }
      });

      await keySession.generateRequest(ev.initDataType, ev.initData);
      _log(drm, 'info', 'FairPlay generateRequest completed');
    } catch (err) {
      _log(drm, 'error', `FairPlay encrypted handler error: ${err.message}`);
    }
  };

  videoEl.addEventListener('encrypted', onEncrypted);

  // 정리 함수 저장
  player._fairPlayCleanup = () => {
    _stopHeartbeat(player);
    videoEl.removeEventListener('encrypted', onEncrypted);
    if (player._fpDvrTimer) {
      clearInterval(player._fpDvrTimer);
      player._fpDvrTimer = null;
    }
    if (player._fpResizeHandler) {
      videoEl.removeEventListener('resize', player._fpResizeHandler);
    }
    processedInitData.clear();
    mediaKeys = null;
  };

  // 3. 네이티브 HLS 재생
  _log(drm, 'info', `FairPlay: loading HLS ${hlsUrl}`);
  videoEl.src = hlsUrl;
  videoEl.play().catch(() => {});

  // === FairPlay 라이브 DVR 표시 (live.html startDvrUpdateHLS + updateDvrUI 패턴) ===
  // 500ms마다 video.seekable 폴링 → 유한한 값이 오면 DVR UI 업데이트
  var fpLiveBadgeCreated = false;
  var fpSeekedToEdge = false;

  function _fmtTime(sec) {
    if (!isFinite(sec)) return '--:--';
    sec = Math.max(0, Math.floor(sec));
    var h = Math.floor(sec / 3600);
    var m = Math.floor((sec % 3600) / 60);
    var s = sec % 60;
    var pad = function (n) { return n < 10 ? '0' + n : '' + n; };
    return h > 0 ? pad(h) + ':' + pad(m) + ':' + pad(s) : pad(m) + ':' + pad(s);
  }

  player._fpDvrTimer = setInterval(function () {
    if (videoEl.duration !== Infinity) return;

    // LIVE 뱃지 (1회)
    if (!fpLiveBadgeCreated) {
      fpLiveBadgeCreated = true;
      _createLiveBadge(player);
      _log(drm, 'info', 'FairPlay Live detected (duration=Infinity)');
    }

    // seekable 범위 읽기 — live.html startDvrUpdateHLS() 패턴 그대로
    if (videoEl.seekable.length === 0) return;
    var seekStart = videoEl.seekable.start(0);
    var seekEnd = videoEl.seekable.end(videoEl.seekable.length - 1);
    if (!isFinite(seekEnd) || !isFinite(seekStart)) return;

    var dvrWindow = seekEnd - seekStart;
    if (dvrWindow <= 0) return;

    // 첫 유한 값 획득 시 라이브 에지로 시크 (1회)
    if (!fpSeekedToEdge) {
      fpSeekedToEdge = true;
      videoEl.currentTime = Math.max(seekEnd - 2, seekStart);
      _log(drm, 'info', 'FairPlay DVR: window=' + dvrWindow.toFixed(0) + 's, seeked to edge');
    }

    // live.html updateDvrUI() 패턴 그대로
    var behindSec = seekEnd - videoEl.currentTime;
    var position = videoEl.currentTime - seekStart;
    if (position < 0) position = 0;

    // StarPlyr 내부 상태 업데이트
    player.liveduration = dvrWindow;
    player._seekRangeStart = seekStart;

    // 직접 DOM 업데이트
    var els = player.elements;
    if (!els || !els.display) return;
    if (els.progress) els.progress.removeAttribute('hidden');
    if (els.display.currentTime) {
      els.display.currentTime.removeAttribute('hidden');
      els.display.currentTime.innerText = _fmtTime(position);
    }
    if (els.display.duration) {
      els.display.duration.removeAttribute('hidden');
      els.display.duration.innerText = _fmtTime(dvrWindow);
    }
    if (els.inputs && els.inputs.seek) {
      els.inputs.seek.setAttribute('aria-valuemax', dvrWindow);
    }

    // LIVE 뱃지 상태
    if (player._liveBadge && player._liveBadgeLabel) {
      var isBehind = behindSec > 10;
      player._liveBadge.className = 'plyr__live-badge ' + (isBehind ? 'is-behind' : 'is-live');
      player._liveBadgeLabel.textContent = isBehind ? 'GO LIVE' : 'LIVE';
    }
  }, 500);

  _log(drm, 'success', 'FairPlay native HLS initialized');

  // Heartbeat 시작
  _startHeartbeat(player);

  // 4. 마스터 플레이리스트 파싱 → 화질 목록 추출
  // CDN 서명 URL(GCS 등 외부)에는 Authorization 헤더 불필요 — CORS preflight 실패 방지
  const isSameOriginManifest = hlsUrl.startsWith('/') || (drm.baseUrl && hlsUrl.startsWith(drm.baseUrl));
  const authHeaders = {};
  if (token && isSameOriginManifest) authHeaders['Authorization'] = `Bearer ${token}`;
  const { qualities, masterText, baseUrl: masterBaseUrl } = await _fetchMasterPlaylist(hlsUrl, authHeaders);
  _log(drm, 'info', `FairPlay: parsed ${qualities.length} quality levels from m3u8`);

  if (qualities.length <= 1) {
    _log(drm, 'info', 'FairPlay: single quality — ABR only');
    return { default: 0, options: [0], forced: false, onChange: null };
  }

  // 5. 화질 옵션 구성
  const heights = [...new Set(qualities.map((q) => q.height))];
  heights.sort((a, b) => b - a);
  heights.unshift(0); // 0 = Auto
  _log(drm, 'info', `FairPlay qualities: ${heights.join(', ')}`);

  // 마스터 playlist에서 헤더+미디어 태그 추출 (variant 필터링 시 재사용)
  // #EXT-X-MEDIA (오디오/자막) URI를 절대경로로 변환 — data: URI 컨텍스트에서 상대경로 해석 불가
  const masterLines = masterText.split('\n');
  const headerLines = [];
  for (var mi = 0; mi < masterLines.length; mi++) {
    var trimmed = masterLines[mi].trim();
    if (!trimmed) continue;
    // variant 스트림 라인과 그 URL은 건너뜀
    if (trimmed.startsWith('#EXT-X-STREAM-INF:')) {
      mi++; // variant URL 라인도 스킵
      continue;
    }
    // #EXT-X-MEDIA 태그: URI를 절대경로로 변환
    if (trimmed.startsWith('#EXT-X-MEDIA:')) {
      trimmed = trimmed.replace(/URI="([^"]+)"/, function (match, uri) {
        if (uri.startsWith('http')) return match;
        try { return 'URI="' + new URL(uri, hlsUrl).href + '"'; } catch (_) { return match; }
      });
    }
    headerLines.push(trimmed);
  }

  let currentSelectedQuality = 0; // 0 = Auto
  const masterUrl = hlsUrl;

  // 필터링된 마스터 playlist data: URI 생성
  // 오디오 #EXT-X-MEDIA 참조를 유지하면서 선택한 해상도의 variant만 포함
  function buildFilteredDataUri(targetHeight) {
    const selected = qualities
      .filter(function (q) { return q.height === targetHeight; })
      .sort(function (a, b) { return b.bandwidth - a.bandwidth; })[0];
    if (!selected) return null;

    // variant URL을 절대경로로 변환
    var absVariantUrl = selected.variantLine.startsWith('http')
      ? selected.variantLine
      : masterBaseUrl + selected.variantLine;

    var playlist = headerLines
      .concat([selected.streamInfLine, absVariantUrl])
      .join('\n');

    return 'data:application/vnd.apple.mpegurl;charset=utf-8,' + encodeURIComponent(playlist);
  }

  player.updateQuality = async (newQuality) => {
    const currentTime = videoEl.currentTime;
    const wasPaused = videoEl.paused;
    currentSelectedQuality = newQuality;

    // ── 1. DRM + 비디오 엘리먼트 리셋 ──
    processedInitData.clear();
    mediaKeys = null;
    videoEl.pause();
    videoEl.removeAttribute('src');
    videoEl.load();

    try {
      await videoEl.setMediaKeys(null);
    } catch (_) {}

    // ── 2. 새 소스 설정 ──
    let newUrl;
    if (newQuality === 0) {
      _log(drm, 'info', 'FairPlay: switching to Auto (master playlist)');
      newUrl = masterUrl;
    } else {
      // 필터링된 마스터 playlist data: URI (오디오 참조 유지 + 절대경로)
      newUrl = buildFilteredDataUri(newQuality);
      if (!newUrl) return;
      _log(drm, 'info', `FairPlay: switching to ${newQuality}p (filtered master playlist)`);
    }

    videoEl.src = newUrl;

    // ── 3. canplay 후 재생 위치 복원 ──
    const onCanPlay = () => {
      videoEl.removeEventListener('canplay', onCanPlay);
      if (currentTime > 0) videoEl.currentTime = currentTime;
      if (!wasPaused) videoEl.play().catch(() => {});
      _log(drm, 'success', `FairPlay: quality switched to ${newQuality === 0 ? 'Auto' : newQuality + 'p'}`);
    };
    videoEl.addEventListener('canplay', onCanPlay);
  };

  // 6. resize 이벤트로 현재 재생 화질 추적 (ABR 전환 감지)
  player._fpResizeHandler = () => {
    const height = videoEl.videoHeight;
    if (!height) return;
    player.hlsquality = height;

    const isAuto = currentSelectedQuality === 0;
    const settingMenu = document.querySelector(
      ".plyr__menu__container [data-plyr='settings'][value='quality'] span span",
    );
    if (settingMenu && isAuto) {
      settingMenu.innerHTML = `Auto (${height}p)`;
    }
    const qualityMenu = document.querySelector(".plyr__menu__container [data-plyr='quality'][value='0'] span");
    if (qualityMenu) {
      qualityMenu.innerHTML = isAuto ? `Auto (${height}p)` : 'Auto';
    }
  };
  videoEl.addEventListener('resize', player._fpResizeHandler);

  return {
    default: 0,
    options: heights,
    forced: true,
    onChange: (e) => player.updateQuality(e),
  };
}

// ---------------------------------------------------------------------------
// FairPlay DVR: progress bar + 시간 표시 강제 복원
// durationUpdate()가 liveduration 설정 전에 이미 숨긴 경우를 보완
// ---------------------------------------------------------------------------

function _forceDvrUi(player, dvrWindow) {
  var els = player.elements;
  if (!els) return;
  // progress bar 표시
  if (els.progress) els.progress.removeAttribute('hidden');
  // currentTime 표시
  if (els.display && els.display.currentTime) els.display.currentTime.removeAttribute('hidden');
  // duration 표시 + 값 갱신
  if (els.display && els.display.duration) {
    els.display.duration.removeAttribute('hidden');
    var h = Math.floor(dvrWindow / 3600);
    var m = Math.floor((dvrWindow % 3600) / 60);
    var s = Math.floor(dvrWindow % 60);
    var pad = function (n) { return n < 10 ? '0' + n : '' + n; };
    els.display.duration.innerText = h > 0 ? pad(h) + ':' + pad(m) + ':' + pad(s) : pad(m) + ':' + pad(s);
  }
  // seek input range max 갱신
  if (els.inputs && els.inputs.seek) {
    els.inputs.seek.setAttribute('aria-valuemax', dvrWindow);
  }
}

// ---------------------------------------------------------------------------
// LIVE 뱃지 (좌측 상단, 컨트롤러와 동기 표시/숨김)
// ---------------------------------------------------------------------------

function _createLiveBadge(player) {
  if (player._liveBadge) return;

  // CSS 1회 주입
  if (!document.getElementById('plyr-live-badge-style')) {
    const style = document.createElement('style');
    style.id = 'plyr-live-badge-style';
    style.textContent = [
      '.plyr--hide-controls .plyr__live-badge { opacity: 0 !important; }',
      '.plyr__live-badge { cursor: pointer; user-select: none; }',
      '.plyr__live-badge.is-live { background: #e91e63 !important; }',
      '.plyr__live-badge.is-behind { background: #666 !important; }',
    ].join('\n');
    document.head.appendChild(style);
  }

  const dotStyle = 'display:inline-block;width:8px;height:8px;border-radius:50%;margin-right:5px;vertical-align:middle;';

  const badge = document.createElement('div');
  badge.className = 'plyr__live-badge is-live';
  badge.style.cssText = 'position:absolute;top:10px;left:10px;z-index:20;' +
    'color:#fff;padding:3px 10px;border-radius:3px;' +
    'font-size:12px;font-weight:bold;letter-spacing:1px;' +
    'transition:opacity .3s ease, background .3s ease;';

  const dot = document.createElement('span');
  dot.style.cssText = dotStyle + 'background:#fff;';
  const label = document.createTextNode('LIVE');
  badge.appendChild(dot);
  badge.appendChild(label);

  // Go Live 클릭
  badge.addEventListener('click', () => {
    const sp = player.shaka;
    if (sp && sp.isLive()) {
      const range = sp.seekRange();
      player.media.currentTime = range.end - 2;
    } else if (player.liveduration && player.media && player.media.seekable.length > 0) {
      const end = player.media.seekable.end(player.media.seekable.length - 1);
      if (isFinite(end) && end > 2) player.media.currentTime = end - 2;
    }
  });

  const container = player.elements ? player.elements.container : null;
  if (container) {
    container.appendChild(badge);
    player._liveBadge = badge;
    player._liveBadgeLabel = label;
  }

  // 라이브 에지 추적: 배지 상태 업데이트
  let wasBehind = false;
  player._liveBadgeTimer = setInterval(() => {
    if (!player._liveBadge || !player.media) return;
    const sp = player.shaka;
    let behind = 0;
    if (sp && sp.isLive()) {
      const range = sp.seekRange();
      behind = range.end - player.media.currentTime;
    } else if (player.liveduration && player.media.seekable.length > 0) {
      const end = player.media.seekable.end(player.media.seekable.length - 1);
      behind = end - player.media.currentTime;
    } else {
      return;
    }
    const isBehind = behind > 10;
    if (isBehind !== wasBehind) {
      wasBehind = isBehind;
      badge.className = 'plyr__live-badge ' + (isBehind ? 'is-behind' : 'is-live');
      player._liveBadgeLabel.textContent = isBehind ? 'GO LIVE' : 'LIVE';
    }
  }, 1000);
}

function _removeLiveBadge(player) {
  if (player._liveBadgeTimer) {
    clearInterval(player._liveBadgeTimer);
    player._liveBadgeTimer = null;
  }
  if (player._liveBadge) {
    player._liveBadge.remove();
    player._liveBadge = null;
  }
}

// ---------------------------------------------------------------------------
// Shaka Player 공통 초기화 (ClearKey DRM / non-DRM 공용)
// ---------------------------------------------------------------------------

function _initShaka(player, url, withDrm) {
  const drm = withDrm ? player.config.drm : null;

  return new Promise((resolve, reject) => {
    if (!url) {
      _log(drm, 'error', 'URL이 비어있습니다');
      return reject(new Error('empty url'));
    }

    // 기존 인스턴스 정리
    _stopHeartbeat(player);
    if (player._dvrTimer) { clearInterval(player._dvrTimer); player._dvrTimer = null; }
    _removeLiveBadge(player);
    if (player.shaka) {
      player.shaka.destroy();
      player.shaka = null;
    }
    if (player._fairPlayCleanup) {
      player._fairPlayCleanup();
      player._fairPlayCleanup = null;
    }

    shaka.polyfill.installAll();
    _log(drm, 'info', 'Shaka polyfill installed');

    const sp = new shaka.Player();
    _log(drm, 'info', `Attaching to <video> (readyState=${player.media ? player.media.readyState : 'N/A'})`);

    sp.attach(player.media)
      .then(() => {
        player.shaka = sp;
        _log(drm, 'info', 'Shaka attached to <video>');

        if (withDrm) {
          const baseUrl = drm.baseUrl || '';
          const drmType = (drm.drmType || 'clearkey').toLowerCase();

          if (drmType === 'widevine') {
            _log(drm, 'info', `DRM mode: Widevine, baseUrl=${baseUrl}`);
            _configureWidevine(sp, drm, baseUrl);
            _registerWidevineNetworkFilters(sp, drm);
            _log(drm, 'info', 'Widevine DRM configured');
          } else {
            _log(drm, 'info', `DRM mode: ClearKey, baseUrl=${baseUrl}`);
            _configureClearKey(sp, drm, baseUrl);
            _registerNetworkFilters(sp, drm);
            _log(drm, 'info', 'ClearKey DRM configured');
          }
        } else {
          sp.configure({ abr: { enabled: true } });
        }

        sp.addEventListener('error', (event) => {
          const detail = event.detail || event;
          _log(drm, 'error', `Shaka error: code=${detail.code}, category=${detail.category}`);
          console.error('Shaka Player error', detail);
        });

        const manifestUrl = withDrm ? _resolveManifestUrl(drm, false, url) : url;
        _log(drm, 'info', `Loading manifest: ${manifestUrl}`);

        sp.load(manifestUrl)
          .then(() => {
            _log(drm, 'success', 'Manifest loaded successfully');

            // 라이브 감지: DVR seekbar를 위해 liveduration + seekRange 저장
            if (sp.isLive()) {
              const range = sp.seekRange();
              player.liveduration = range.end - range.start;
              player._seekRangeStart = range.start;
              player.media.currentTime = range.end - 2; // 라이브 에지로 시크
              _log(drm, 'info', `Live DVR: window=${player.liveduration.toFixed(0)}s`);
              _createLiveBadge(player);

              // 주기적 DVR 윈도우 + seekRange 업데이트 + UI 갱신
              player._dvrTimer = setInterval(() => {
                if (!sp || !sp.isLive()) return;
                const r = sp.seekRange();
                player.liveduration = r.end - r.start;
                player._seekRangeStart = r.start;
                // duration 표시 갱신
                if (player.elements && player.elements.display && player.elements.display.duration) {
                  player.elements.display.duration.innerText =
                    new Date(player.liveduration * 1000).toISOString().substr(11, 8).replace(/^00:/, '');
                }
                // seek range max 갱신
                if (player.elements && player.elements.inputs && player.elements.inputs.seek) {
                  player.elements.inputs.seek.setAttribute('aria-valuemax', player.liveduration);
                }
              }, 2000);
            }

            // Heartbeat 시작 (DRM 모드에서만)
            if (withDrm) _startHeartbeat(player);

            const quality = _buildQuality(sp, player);
            _registerAdaptationHandler(sp, player);
            resolve(quality);
          })
          .catch((err) => {
            _log(drm, 'error', `Shaka load failed: code=${err.code}, message=${err.message || ''}`);
            console.error('Shaka load error', err);
            reject(err);
          });
      })
      .catch((err) => {
        _log(drm, 'error', `Shaka attach failed: ${err.message || err}`);
        console.error('Shaka attach error', err);
        reject(err);
      });
  });
}

function _resolveManifestUrl(drm, isFairPlay, fallbackUrl) {
  if (drm && drm.manifest) {
    const path = isFairPlay ? drm.manifest.hls : drm.manifest.dash;
    if (path) {
      const baseUrl = drm.baseUrl || '';
      return path.startsWith('http') ? path : baseUrl + path;
    }
  }
  return fallbackUrl;
}

function _buildQuality(sp, player) {
  const tracks = sp.getVariantTracks();
  if (!tracks || !tracks.length) {
    return { default: 0, options: [0], forced: false, onChange: null };
  }

  const heights = [...new Set(tracks.map((t) => t.height).filter(Boolean))];
  heights.sort((a, b) => b - a);
  heights.unshift(0); // AUTO

  player.updateQuality = (newQuality) => {
    const q = Number(newQuality);
    if (q === 0 || Number.isNaN(q)) {
      sp.configure({ abr: { enabled: true } });
    } else {
      sp.configure({ abr: { enabled: false } });
      const target = sp.getVariantTracks().find((t) => t.height === q);
      if (target) sp.selectVariantTrack(target, true);
    }
  };

  return {
    default: 0,
    options: heights,
    forced: true,
    onChange: (e) => player.updateQuality(e),
  };
}

function _updateAutoLabel(sp, player) {
  const active = sp.getVariantTracks().find((t) => t.active);
  if (!active) return;
  player.hlsquality = active.height;

  const isAuto = sp.getConfiguration().abr.enabled;
  const settingMenu = document.querySelector(
    ".plyr__menu__container [data-plyr='settings'][value='quality'] span span",
  );
  if (settingMenu && isAuto) {
    settingMenu.innerHTML = `Auto (${active.height}p)`;
  }
  const qualityMenu = document.querySelector(".plyr__menu__container [data-plyr='quality'][value='0'] span");
  if (qualityMenu) {
    qualityMenu.innerHTML = isAuto ? `Auto (${active.height}p)` : 'Auto';
  }
}

function _registerAdaptationHandler(sp, player) {
  // ABR 트랙 전환 시 라벨 업데이트
  sp.addEventListener('adaptation', () => _updateAutoLabel(sp, player));

  // 초기 트랙 선택 후 라벨 표시 (adaptation 이벤트는 초기 선택 시 미발생)
  var videoEl = player.media;
  if (videoEl) {
    var onPlaying = function () {
      videoEl.removeEventListener('playing', onPlaying);
      // 약간의 딜레이 후 UI 갱신 (메뉴 DOM 렌더링 대기)
      setTimeout(function () { _updateAutoLabel(sp, player); }, 500);
    };
    videoEl.addEventListener('playing', onPlaying);
  }
}

// ---------------------------------------------------------------------------
// Heartbeat Manager (120초 간격 → POST /api/playback/heartbeat, ClearKey 전용)
// ---------------------------------------------------------------------------

function _startHeartbeat(player) {
  _stopHeartbeat(player);
  const drm = player.config.drm;
  if (!drm) return;

  const interval = drm.heartbeatInterval != null ? drm.heartbeatInterval : 120000;
  if (interval <= 0) return;

  const baseUrl = drm.baseUrl || '';
  const videoEl = player.media;

  async function send() {
    try {
      const token = typeof drm.getToken === 'function' ? await drm.getToken() : drm.getToken;
      if (!token) return;

      const position = videoEl ? Math.floor(videoEl.currentTime || 0) : 0;
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      };
      if (drm.deviceId) headers['X-Device-ID'] = drm.deviceId;

      await fetch(`${baseUrl}/api/playback/heartbeat`, {
        method: 'POST',
        headers,
        signal: AbortSignal.timeout(5000),
        body: JSON.stringify({
          content_id: drm.contentId || '',
          device_id: drm.deviceId || '',
          user_id: drm.userId || '',
          position,
        }),
      });
    } catch (_) {
      // 하트비트 실패 무시 — 다음 간격에 재시도
    }
  }

  // 즉시 1회 호출 + 주기적 반복
  send();
  player._heartbeatTimer = setInterval(send, interval);
  _log(drm, 'info', `Heartbeat started (${interval / 1000}s interval)`);
}

function _stopHeartbeat(player) {
  if (player._heartbeatTimer) {
    clearInterval(player._heartbeatTimer);
    player._heartbeatTimer = null;
  }
}

// ---------------------------------------------------------------------------
// Plugin export
// ---------------------------------------------------------------------------

const shakaPlugin = {
  // DRM 설정이 있는지 판별
  isdrm() {
    return this.config.drm && this.config.drm.enabled === true;
  },

  // DASH URL 패턴 판별
  isdash(url) {
    if (is.array(url)) {
      const [{ src }] = url;
      url = src;
    }
    if (/^https?:\/\/[a-zA-Z0-9.\/?=_%&:-]+(?:.mpd|\(dash\))/.test(url)) {
      return true;
    }
    return false;
  },

  // DRM 모드: FairPlay(Safari) → 네이티브, Widevine/ClearKey(Chrome) → Shaka
  setup(path) {
    const player = this;
    let url = path;
    if (is.array(path)) {
      const [{ src }] = path;
      url = src;
    }

    const isFairPlay = _detectFairPlay();
    if (isFairPlay) {
      // Safari: Shaka 미사용, 네이티브 HLS + EME FairPlay
      const drm = player.config.drm;
      const hlsUrl = _resolveManifestUrl(drm, true, url);
      return _initFairPlay(player, hlsUrl);
    }

    // Chrome/Firefox: Shaka + Widevine or ClearKey (drm.drmType으로 선택)
    return _initShaka(player, url, true);
  },

  // 일반 DASH 모드: Shaka Player (DRM 없음)
  setupDash(path) {
    const player = this;
    let url = path;
    if (is.array(path)) {
      const [{ src }] = path;
      url = src;
    }
    return _initShaka(player, url, false);
  },
};

export default shakaPlugin;
